<script setup lang="ts">
import {NotificationSequenceDetails} from "@/notification-sequences/interfaces/NotificationSequence";
import {computed, ref} from "vue";
import {notificationSequenceApi} from "@/container";
import NotificationSequencePreview
    from "@/notification-sequences/components/notification-sequence-preview/NotificationSequencePreview.vue";
import {Send} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Customer from "@/customer/interfaces/Customer";
import formatHelper from "@/core/helpers/formatHelper";
import {useToastStore} from "@/stores/toast.store";
import {Dialog, DialogTrigger} from "@/core/components/ui/dialog";
import PreCertificationDialogContent
    from "@/customer-certification/components/pre-certification-dialog/PreCertificationDialogContent.vue";
import {isAxiosError} from "axios";

const props = defineProps<{
    sequence: NotificationSequenceDetails;
    customer: Customer;
    consumerKey: string;
}>();

const emits = defineEmits<{
    (e: 'started'): void;
    (e: 'certified'): void
}>();

const {showSuccess, showError} = useToastStore();

const isStarting = ref<boolean>(false);

const uncertified = computed(() => {
    return props.customer?.status === 'none';
});

const startSequence = async () => {
    isStarting.value = true;

    try {
        await notificationSequenceApi().createInstance(props.sequence.id, {
            consumer_key: props.consumerKey
        });

        showSuccess({
            content: 'La séquence a été démarrée avec succès'
        });
        emits('started');
    } catch (e) {
        if (isAxiosError(e) && e.response?.status === 409) {
            showError({
                content: 'La séquence a déjà été démarrée pour ce patient'
            });
        } else {
            showError({
                content: 'Une erreur est survenue lors du démarrage de la séquence'
            });
        }
    } finally {
        isStarting.value = false;
    }
};
</script>

<template>
  <NotificationSequencePreview :sequence="sequence">
    <Dialog v-if="uncertified && customer">
      <DialogTrigger>
        <BaseButton primary density="comfortable">
          Démarrer la séquence
          <Send class="ml-2 w-4" />
        </BaseButton>
      </DialogTrigger>
      <PreCertificationDialogContent :customer="customer" :on-certified="() => $emit('certified')" />
    </Dialog>
    <BaseButton
      v-else-if="customer?.information?.phone"
      primary
      density="comfortable"
      class="self-center"
      :loading="isStarting"
      @click="startSequence"
    >
      Démarrer pour {{ formatHelper.readablePhone(customer.information.phone) }}
      <Send class="ml-2 w-4" />
    </BaseButton>
    <p v-else class="text-sm text-muted-foreground self-center">
      Numéro de téléphone requis pour démarrer la séquence
    </p>
  </NotificationSequencePreview>
</template>
